import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  showPasswordIcon,
  hidePasswordIcon,
} from '../../utils/icons';
import LoginAuth from '../../services/auth.service';
import Auth from '../../components/layouts/auth';
import { validateForm } from '../../utils/validations';
import IpService from '../../services/Ip.service';
import {
  isMobile, isTablet, isBrowser,
  browserName, browserVersion,
  osName, osVersion, deviceType,
} from 'react-device-detect';
import ClientService from '../../services/client.service';
import { setClientId } from '../../feature/client.slice';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../../feature/loading.slice';
import { setMessage } from '../../feature/message.slice';
import messages from '../../utils/meesage';
import { safeRedirect } from '../../utils/safeRedirect';

const SignIn = () => {
  /* ─────────────── store hooks ─────────────── */
  const isLoading = useSelector((state) => state.loading.loading);
  const message   = useSelector((state) => state.message.message);
  const dispatch  = useDispatch();

  /* ─────────────── router hooks ─────────────── */
  const navigate  = useNavigate();
  const location  = useLocation();
  const params    = new URLSearchParams(location.search);

  /* ── read ?redirect=, rejecting anything that is not a path in this app ── */
  const redirectParam = params.get('redirect');          // encoded value
  const redirectTo    = safeRedirect(redirectParam);     // "/chats?threadId=24"

  /* ── still allow pre-filled email ── */
  const emailFromUrl  = params.get('email') || '';

  /* ─────────────── local state ─────────────── */
  const [ip, setIp] = useState('');
  const [deviceInfo, setDeviceInfo] = useState([]);
  const [formData, setFormData] = useState({
    email: emailFromUrl,
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors]           = useState({});
  const [apiError, setApiError]       = useState(false);
  const [apiMsg, setApiMsg]           = useState('');
  const [buttonClick, setButtonClick] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* … IP + device info effect (unchanged) … */
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const { ip } = await IpService.getIp();
        setIp(ip);
      } catch (e) { console.error(e); }
    };
    fetchIp();

    setDeviceInfo([
      { label: 'Browser',     value: `${browserName} ${browserVersion}` },
      { label: 'OS',          value: `${osName} ${osVersion}` },
      { label: 'Device Type', value: deviceType },
      { label: 'Is Mobile',   value: isMobile  ? 'Yes' : 'No' },
      { label: 'Is Tablet',   value: isTablet  ? 'Yes' : 'No' },
      { label: 'Is Browser',  value: isBrowser ? 'Yes' : 'No' },
    ]);
  }, []);

  /* ─────────────── handlers ─────────────── */
  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [id]: type === 'checkbox' ? checked : value }));
  };
  const togglePasswordVisibility = () => setShowPassword((v) => !v);

  const handleSignIn = async (e) => {
    e.preventDefault();
    const formErrors = validateForm(formData);
    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) return;

    const payload = {
      email:       formData.email,
      password:    formData.password,
      rememberMe:  false,
      ipAddress:   ip,
      deviceInfo,
    };
    setButtonClick(true);
    try {
      dispatch(setLoading(true));
      dispatch(setMessage(messages.loading));

      const res = await LoginAuth.login(payload);
      /* get client IDs etc. */
      const id = res.data.user.user.id;
      await fetchClientAndStore(id);

      setApiError(false);
      setApiMsg(res.data.message);
     

      /* ⬅️ CHANGED: go to the intended page */
      setButtonClick(false);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setApiError(true);
      
      setApiMsg(err.response?.data?.error || 'Login failed');
      setTimeout(() => { 
        setApiError(false); 
        setApiMsg(''); 
        setButtonClick(false);
      }, 3000);
       
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchClientAndStore = async (userId) => {
    try {
      const { clientIds } = await ClientService.getclient({ userId });
      localStorage.setItem('client', JSON.stringify(clientIds));
      dispatch(setClientId(clientIds[0].id));
    } catch (e) { console.error(e); }
  };

  /* ─────────────── UI ─────────────── */
  return (
    <Auth
      pageTitle="Sign In"
      buttonClick={buttonClick}
      ApiError={apiError}
      ApiErrorMessage={apiMsg}
    >
      <Form onSubmit={handleSignIn}>
        {/* Email field */}
        <Form.Group className="form-group mb-3">
          <div className="text-center"><Form.Label htmlFor="email">Email</Form.Label></div>
          <Form.Control
            id="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            isInvalid={!!errors.email}
            placeholder=" "
          />
          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
        </Form.Group>

        {/* Password field */}
        <Form.Group className="form-group">
          <div className="text-center"><Form.Label htmlFor="password">Password</Form.Label></div>
          <div className="position-relative">
            <Form.Control
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              isInvalid={!!errors.password}
            />
            <span
              className="position-absolute password-toggle-icon"
              onClick={togglePasswordVisibility}
            >
              <img
                src={showPassword ? hidePasswordIcon : showPasswordIcon}
                width={20}
                style={{ opacity: 0.3 }}
              />
            </span>
            <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
          </div>
        </Form.Group>

        {/* Links */}
        <div className="d-flex justify-content-between py-3">
          <Link
            to={`/forget-password?email=${encodeURIComponent(formData.email)}&redirect=${encodeURIComponent(redirectParam || '')}`}
            className="forgot-password-link"
          >
            Forgot Password?
          </Link>
        </div>

        <div className="text-center">
          <Button
            className="btn-expanded"
            type="submit"
            variant="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? message : 'Sign In'}
          </Button>
        </div>

        <div className="text-center pt-3">
          <Link
            to={`/login-otp?email=${encodeURIComponent(formData.email)}&redirect=${encodeURIComponent(redirectParam || '')}`}
            className="secondary-cta"
          >
            or Login with OTP in Email
          </Link>
        </div>
      </Form>
    </Auth>
  );
};

export default SignIn;
